import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Icon from '@components/Icon';
import { RowAlignedItems } from '@components/LinearLayout';
import { RotateIcon } from '@composites/MainMenuItemDesktop/styles';
import { useSpring } from '@react-spring/web';
import { useTranslation } from 'react-i18next';

const DropdownContainer = styled.div<{
  $minWidth?: string;
  $maxWidth?: string;
}>`
  position: relative;
  display: inline-block;
  min-width: ${({ $minWidth }) => $minWidth ?? 'auto'};
  max-width: ${({ $maxWidth }) => $maxWidth ?? 'auto'};
`;

const Input = styled.input<{
  $isOpen: boolean;
  $fontSize: string;
  $padding: string;
  $border?: string;
  $borderRadius?: string;
  $color?: string;
  $justifyContent?: string;
  $backgroundColor?: string;
}>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  padding: ${({ $padding }) => $padding};
  color: ${({ $color }) => $color};
  font-size: ${({ $fontSize }) => $fontSize};
  width: 100%;
  box-sizing: border-box;
  text-align: ${({ $justifyContent }) => $justifyContent};
  cursor: pointer;
  border: ${({ $border }) => $border ?? 'none'};
  border-radius: ${({ $borderRadius }) => $borderRadius ?? '0'};
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.06);
  ${({ $isOpen, $border }) =>
    $isOpen
      ? css`
          max-height: 29rem;
          pointer-events: auto;
          border-top: ${$border};
          border-left: ${$border};
          border-right: ${$border};
        `
      : css`
          max-height: 29rem;
          pointer-events: auto;
        `}
`;

const DropdownMenu = styled.ul<{
  $isOpen: boolean;
  $borderRadius?: string;
  $border?: string;
  $dropdownBorder?: string;
}>`
  position: absolute;
  top: calc(100% - 0.4rem);
  left: 0;
  background: white;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 500ms ease-in-out;
  pointer-events: none;
  z-index: ${({ $isOpen }) => ($isOpen ? 10 : 2)};
  border-left: ${({ $border }) => $border};
  border-right: ${({ $border }) => $border};
  border-bottom: ${({ $border, $isOpen }) => ($isOpen ? $border : 'none')};
  border-bottom-right-radius: ${({ $borderRadius }) => $borderRadius ?? '0'};
  border-bottom-left-radius: ${({ $borderRadius }) => $borderRadius ?? '0'};
  border: ${({ $dropdownBorder }) => $dropdownBorder};
  overflow-y: auto;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      max-height: 29rem;
      pointer-events: auto;
    `}
`;

const DropdownItem = styled.li<{
  $fontSize: string;
  $padding: string;
  $justifyContent?: string;
  $backgroundColor?: string;
  $color?: string;
  $highlighted?: boolean;
}>`
  padding: ${({ $padding }) => $padding};
  color: ${({ $color }) => $color};
  text-align: ${({ $justifyContent }) => $justifyContent};
  cursor: pointer;
  font-size: ${({ $fontSize }) => $fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.weight400};
  line-height: ${({ theme }) => theme.lineHeight.height150};
  background-color: ${({ $highlighted, $backgroundColor }) =>
    $highlighted ? 'rgba(0, 0, 0, 0.1)' : $backgroundColor};
`;

const IconContainer = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
`;

interface WeDropDownProps {
  options: Array<{ value: string; label: string }>;
  fontSize: string;
  padding: string;
  onChange?: (value: string) => void;
  value?: string;
  placeholder?: string;
  border?: string;
  borderRadius?: string;
  dropdownBorder?: string;
  color?: string;
  hoverColor?: string;
  defaultValue?: string;
  disabled?: boolean;
  minWidth?: string;
  maxWidth?: string;
  focusBorder?: string;
  justifyContent?: string;
  backgroundColor?: string;
  selectedValueMode?: boolean;
}

const WeDropDown = forwardRef(
  (
    {
      options,
      placeholder,
      border,
      borderRadius,
      dropdownBorder,
      fontSize,
      padding,
      color,
      hoverColor,
      value,
      onChange,
      defaultValue,
      disabled,
      minWidth,
      maxWidth,
      focusBorder,
      justifyContent,
      backgroundColor,
      selectedValueMode,
    }: WeDropDownProps,
    ref
  ) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [textColor, setTextColor] = useState(color);
    const [inputValue, setInputValue] = useState<string>(value ?? '');
    const [isFocused, setIsFocused] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const arrowRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);

    const selectedLabel = useMemo(
      () =>
        options
          ?.find(option => option.value.trim() === inputValue)
          ?.label.trim() ?? placeholder,
      [inputValue, options, placeholder]
    );

    const displayedOptions = useMemo(() => {
      return selectedValueMode
        ? options?.filter(
            option => option.label.trim() !== selectedLabel?.trim()
          )
        : options;
    }, [options, selectedLabel, selectedValueMode]);

    const toggleDropdown = () => {
      setIsOpen(prev => !prev);
      setFocusedIndex(0);
    };

    const props = useSpring({
      from: { transform: 'rotateZ(0deg)' },
      to: { transform: 'rotateZ(-180deg)' },
      config: { duration: 600 },
      reverse: !isOpen,
    });

    const handleFocus = () => {
      setIsFocused(true);
    };
    const handleBlur = () => {
      setIsFocused(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        setIsOpen(true);
        setFocusedIndex(0);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % displayedOptions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex(
          prev => (prev - 1 + displayedOptions.length) % displayedOptions.length
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
          const selected = displayedOptions[focusedIndex];
          onChange?.(selected.value);
          setInputValue(selected.value);
          setIsOpen(false);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      if (arrowRef.current) {
        const inputElement = arrowRef.current
          .previousSibling as HTMLInputElement | null;
        if (inputElement) {
          const inputRect = inputElement.getBoundingClientRect();
          const valueLength = selectedLabel?.length ?? 0;
          arrowRef.current.style.left = `${inputRect.left + valueLength * 10 + 20}px`;
        }
      }
    }, [selectedLabel?.length]);

    return (
      <DropdownContainer $minWidth={minWidth} $maxWidth={maxWidth}>
        <RowAlignedItems>
          <Input
            type="text"
            value={!selectedValueMode && isOpen ? '' : selectedLabel}
            onClick={toggleDropdown}
            onKeyDown={handleKeyDown}
            placeholder={isOpen ? '' : (placeholder ?? t('select'))}
            $border={isFocused ? focusBorder : border}
            $borderRadius={borderRadius}
            $isOpen={isOpen}
            $padding={padding}
            $fontSize={fontSize}
            $backgroundColor={backgroundColor}
            $color={color}
            $justifyContent={justifyContent}
            readOnly
            onFocus={handleFocus}
            onBlur={handleBlur}
            onMouseOver={() => {
              setTextColor(hoverColor);
            }}
            onMouseOut={() => {
              setTextColor(color);
            }}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          />
          {justifyContent === 'center' && (
            <IconContainer ref={arrowRef} onClick={toggleDropdown}>
              <RotateIcon style={props}>
                <Icon
                  size={24}
                  color={textColor}
                  name="event-pages_dropdown-arrow"
                  indentLess
                />
              </RotateIcon>
            </IconContainer>
          )}
        </RowAlignedItems>
        <DropdownMenu
          ref={dropdownRef}
          role="listbox"
          $isOpen={isOpen}
          $borderRadius={borderRadius}
          $border={border}
          $dropdownBorder={dropdownBorder}
        >
          {displayedOptions.map((option, idx) => (
            <DropdownItem
              key={option.value}
              role="option"
              aria-selected={focusedIndex === idx}
              $fontSize={fontSize}
              $padding={padding}
              $justifyContent={justifyContent}
              $color={color}
              $backgroundColor={backgroundColor}
              $highlighted={focusedIndex === idx}
              onClick={() => {
                onChange?.(option.value);
                setInputValue(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
        {justifyContent !== 'center' && (
          <IconContainer onClick={toggleDropdown}>
            <RotateIcon style={props}>
              <Icon
                size={24}
                color={textColor}
                name="event-pages_dropdown-arrow"
                indentLess
              />
            </RotateIcon>
          </IconContainer>
        )}
      </DropdownContainer>
    );
  }
);

WeDropDown.displayName = 'WeDropDown';
export default WeDropDown;
